import { Component, OnInit } from '@angular/core';
import { PatientContext } from 'app/mpi/lib/patient/services/patient-context';
import { ClinicalDocument, ClinicalDocumentSearchParams } from 'app/integration/document/services/clinical-document';
import moment from 'moment';
import { ActivatedRoute } from '@angular/router';
import { Patient } from 'app/mpi/lib/patient/services/patient';
import { ClinicalDocumentService } from 'app/integration/document/services/clinical-document.service';
import { CodeSystemConcept } from 'app/mdm/terminology-lib/services/codesystem';
import { CodeSystemService } from 'app/mdm/terminology-lib/services/codesystem.service';
import { TranslateService } from '@ngx-translate/core';
import { PatientService } from 'app/mpi/lib/patient/services/patient.service';

const DOCUMENTS_PER_PAGE = 50;

@Component({
  selector: 'hw-document-list',
  templateUrl: './clinical-document-list.component.html',
})
export class ClinicalDocumentListComponent implements OnInit {
  public loading = false;
  public params = new ClinicalDocumentSearchParams();
  public patient: Patient;
  public documents: ClinicalDocument[] = [];
  public total = 0;
  public pageIndex = 1;
  public itemsPerPage = DOCUMENTS_PER_PAGE;

  public documentTypes: { [key: string]: CodeSystemConcept };

  constructor(
    public patientContext: PatientContext,
    private route: ActivatedRoute,
    private patientService: PatientService,
    private documentService: ClinicalDocumentService,
    private codeSystemService: CodeSystemService,
    private translateService: TranslateService
  ) {}

  public ngOnInit(): void {
    this.params.to = new Date();
    this.params.from = new Date(moment().subtract(6, 'month').valueOf());

    this.params.type = this.route.snapshot.paramMap.get('documentType');
    const patientId = this.route.snapshot.paramMap.get('patientId');
    if (patientId) {
      this.patientService.getPatient(patientId).subscribe((resp) => {
        this.patient = resp;
        this.params.patient = this.patient.id;
      });
    }

    this.codeSystemService.getConcepts('document-type').subscribe((types) => {
      this.documentTypes = types;
    });
  }

  public search(): void {
    this.clearPagination();
    this.runSearch();
  }

  private clearPagination(): void {
    this.pageIndex = 1;
    this.itemsPerPage = DOCUMENTS_PER_PAGE;
  }

  public runSearch(): void {
    this.params.offset = this.itemsPerPage * (this.pageIndex - 1);
    this.params.limit = this.itemsPerPage;
    if (!this.params.patient) {
      // TODO add notification
      this.loading = false;
      return;
    }
    this.loading = true;
    this.documentService.query(this.params).subscribe(
      (resp) => {
        this.documents = resp.data;
        this.itemsPerPage = resp.meta.itemsPerPage;
        this.pageIndex = resp.meta.offset / resp.meta.itemsPerPage + 1;
        this.total = resp.meta.total;
        this.loading = false;
      },
      (error) => {
        this.documents = [];
        this.loading = false;
      }
    );
  }

  public typeOrder(c1: CodeSystemConcept, c2: CodeSystemConcept): number {
    const leftPad = (code) => {
      const dotPos = code.indexOf('.') >= 0 ? code.indexOf('.') : code.length;
      return ' '.repeat(10 - dotPos) + code;
    };
    const code1: string = leftPad(c1.attributes['codeValue'] || '');
    const code2: string = leftPad(c2.attributes['codeValue'] || '');
    return code1.localeCompare(code2);
  }

  public typeTitle = (concept: CodeSystemConcept) => {
    const lang = this.translateService.currentLang;
    return concept.attributes['codeValue'] + ' ' + concept.names[lang];
  };

  public emptyFilter(): void {
    this.params = new ClinicalDocumentSearchParams();
    const patientId = this.route.snapshot.paramMap.get('patientId');
    if (patientId != null) {
      this.patientService.getPatient(patientId).subscribe((resp) => {
        this.patient = resp;
        this.params.patient = this.patient.id;
      });
    } else {
      this.patient = null;
    }
    this.params.to = new Date();
    this.params.from = new Date(moment().subtract(6, 'month').valueOf());
  }

  public onPatientSelected(patient: Patient): void {
    if (patient) {
      this.patient = patient;
      this.params.patient = patient.id;
    } else {
      this.patient = null;
    }
  }

  public openDocument(document: ClinicalDocument): void {
    this.documentService.openDocument(document.type, document.identifier, this.patient.id);
  }

  public onPageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.runSearch();
  }
}
